import ScheduleService from "@/services/scheduleService";
import ScheduleFollowUpService from "@/services/followUpService";
import moment from "moment";

export const ScheduleStore = {
    namespaced: true,
    state: {
        tabs: {
            all_expiring: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "nearest_expiry",
                sortOrder: "1",
                searchText: "",
                selectedEngineer: null,
                fromDate: null,
                toDate: null,
                first: 0,
                selectedScheduleStatus: null,
            },
            booked: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "start_date",
                sortOrder: "1",
                searchText: "",
                selectedEngineer: null,
                fromDate: null,
                toDate: null,
                first: 0,
                selectedScheduleStatus: null,
            },
            unbooked: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "start_date",
                sortOrder: "1",
                searchText: "",
                selectedEngineer: null,
                fromDate: null,
                toDate: null,
                first: 0,
                selectedScheduleStatus: null,
            },
            completed: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "start_date",
                sortOrder: "1",
                searchText: "",
                selectedEngineer: null,
                fromDate: null,
                toDate: null,
                first: 0,
                selectedScheduleStatus: null,
            },
            follow_up: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "created_at",
                sortOrder: "1",
                searchText: "",
                selectedEngineer: null,
                fromDate: null,
                toDate: null,
                first: 0,
                selectedScheduleStatus: null,
            },
        },
        currentTab: "all_expiring",
        isLoading: false,
        activeSchedule: null,
        assignments: [],
        summary: null,
        isSaving: false,
    },
    mutations: {
        SET_TABLE_DATA(state, {tab, data}) {
            state.tabs[tab].tableData = data.data;
            state.tabs[tab].totalRecords = data.count;
        },
        UPDATE_CURRENT_PAGE(state, {tab, page}) {
            state.tabs[tab].currentPage = page;
        },
        UPDATE_ROWS_PER_PAGE(state, {tab, rows}) {
            state.tabs[tab].rowsPerPage = rows;
        },
        UPDATE_SORT_OPTIONS(state, {tab, field, order}) {
            state.tabs[tab].sortField = field;
            state.tabs[tab].sortOrder = order;
        },
        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
            if (state.tabs[currentTab]) {
                state.tabs[currentTab].currentPage = 1;
                state.tabs[currentTab].first = 0;
            }
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        UPDATE_FROM_DATE(state, {tab, fromDate}) {
            state.tabs[tab].fromDate = fromDate;
        },
        UPDATE_TO_DATE(state, {tab, toDate}) {
            state.tabs[tab].toDate = toDate;
        },
        UPDATE_SELECTED_ENGINEER(state, { tab, selectedEngineer }) {
            state.tabs[tab].selectedEngineer = selectedEngineer;
        },
        UPDATE_SEARCH_TEXT(state, {tab, searchText}) {
            state.tabs[tab].searchText = searchText;
        },
        RESET_FIRST_VALUE(state, { tab }) {
            const currentPage = state.tabs[tab].currentPage;
            const rowsPerPage = state.tabs[tab].rowsPerPage;
            state.tabs[tab].first = currentPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
        },
        RESET_CURRENT_PAGE(state, { tab, page = 0 }) {
            state.tabs[tab].currentPage = page;
        },
        UPDATE_SELECTED_STATUS(state, { tab, status }) {
            state.tabs[tab].selectedScheduleStatus = status;
        },
        SET_ACTIVE_SCHEDULE(state, schedule) {
            state.activeSchedule = schedule;
        },
        SET_ASSIGNMENTS(state, assignments) {
            state.assignments = assignments;
        },
        SET_SUMMARY(state, summary) {
            state.summary = summary;
        },
        SET_SAVING(state, isSaving) {
            state.isSaving = isSaving;
        },
        ADD_ASSIGNMENTS(state, newAssignments) {
            state.assignments = [...state.assignments, ...newAssignments];
        },
        UPDATE_ASSIGNMENT(state, updated) {
            const idx = state.assignments.findIndex(a => a.assignment_id === updated.assignment_id);
            if (idx !== -1) {
                state.assignments.splice(idx, 1, updated);
            }
        },
    },
    actions: {
        async fetchTableData({commit, state, dispatch}) {
            commit("SET_LOADING", true);

            commit("SET_TABLE_DATA", { tab: state.currentTab, data: { count: 0, data: [] } });

            const currentTabState = state.tabs[state.currentTab];

            let params = buildParams(currentTabState, state);

            const apiEndpoint = getApiEndpoint(state.currentTab);
            const queryString = buildQueryString(params);

            try {
                let data = await apiEndpoint(`?${queryString}`);
                commit("SET_TABLE_DATA", {tab: state.currentTab, data});
            } catch (error) {
                commit("SET_TABLE_DATA", {tab: state.currentTab, data: []});
                console.error("Error fetching data:", error);
            }  finally {
                commit("SET_LOADING", false);
                dispatch("resetFirstValue");
            }
        },

        updateTableSettings({commit, dispatch, state}, {rows, page}) {
            commit("UPDATE_ROWS_PER_PAGE", {tab: state.currentTab, rows});
            commit("UPDATE_CURRENT_PAGE", {tab: state.currentTab, page: page + 1});
            dispatch("fetchTableData");
        },
        updateSortOptions({commit, dispatch, state}, {field, order}) {
            commit("UPDATE_SORT_OPTIONS", {tab: state.currentTab, field, order});
            dispatch("fetchTableData");
        },
        updateTab({commit, dispatch}, currentTab) {
            commit("UPDATE_CURRENT_TAB", currentTab);
            dispatch("fetchTableData");
        },
        resetFirstValue({ commit, state }) {
            commit("RESET_FIRST_VALUE", { tab: state.currentTab });
        },
        resetCurrentPage({ commit, state }, page = 0) {
            commit("RESET_CURRENT_PAGE", { tab: state.currentTab, page });
        },

        async fetchScheduleById({ commit }, scheduleId) {
            commit("SET_LOADING", true);
            try {
                let data = await ScheduleService.getScheduleById(scheduleId);
                commit("SET_ACTIVE_SCHEDULE", data);
                return data;
            } catch (error) {
                console.error("Error fetching schedule:", error);
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async fetchAssignments({ commit }, scheduleId) {
            try {
                let data = await ScheduleService.listAssignments(scheduleId, "?limit=200");
                commit("SET_ASSIGNMENTS", data.data || []);
                return data;
            } catch (error) {
                console.error("Error fetching assignments:", error);
            }
        },

        async fetchSummary({ commit }, scheduleId) {
            try {
                let data = await ScheduleService.getScheduleSummary(scheduleId);
                commit("SET_SUMMARY", data);
                return data;
            } catch (error) {
                console.error("Error fetching summary:", error);
            }
        },

        async createSchedule({ commit }, payload) {
            commit("SET_SAVING", true);
            try {
                let data = await ScheduleService.createSchedule(payload);
                commit("SET_ACTIVE_SCHEDULE", data.schedule);
                return data;
            } catch (error) {
                throw error;
            } finally {
                commit("SET_SAVING", false);
            }
        },

        async updateSchedule({ commit }, { scheduleId, payload }) {
            commit("SET_SAVING", true);
            try {
                let data = await ScheduleService.updateSchedule(scheduleId, payload);
                commit("SET_ACTIVE_SCHEDULE", data);
                return data;
            } catch (error) {
                throw error;
            } finally {
                commit("SET_SAVING", false);
            }
        },

        async updateScheduleEquipment({ commit }, { scheduleId, payload }) {
            commit("SET_SAVING", true);
            try {
                let data = await ScheduleService.updateScheduleEquipment(scheduleId, payload);
                commit("SET_ACTIVE_SCHEDULE", data);
                return data;
            } catch (error) {
                throw error;
            } finally {
                commit("SET_SAVING", false);
            }
        },

        async updateScheduleDates({ commit }, { scheduleId, payload }) {
            commit("SET_SAVING", true);
            try {
                let data = await ScheduleService.updateScheduleDates(scheduleId, payload);
                commit("SET_ACTIVE_SCHEDULE", data);
                return data;
            } catch (error) {
                throw error;
            } finally {
                commit("SET_SAVING", false);
            }
        },

        async confirmSchedule({ commit }, { scheduleId, payload }) {
            commit("SET_SAVING", true);
            try {
                let data = await ScheduleService.confirmSchedule(scheduleId, payload);
                commit("SET_ACTIVE_SCHEDULE", data);
                return data;
            } catch (error) {
                throw error;
            } finally {
                commit("SET_SAVING", false);
            }
        },

        async cancelSchedule({ commit }, { scheduleId, payload }) {
            try {
                let data = await ScheduleService.cancelSchedule(scheduleId, payload);
                if (data.schedule) {
                    commit("SET_ACTIVE_SCHEDULE", data.schedule);
                }
                return data;
            } catch (error) {
                throw error;
            }
        },

        async addAssignments({ commit }, { scheduleId, payload }) {
            try {
                let data = await ScheduleService.addAssignments(scheduleId, payload);
                if (data.created && data.created.length) {
                    commit("ADD_ASSIGNMENTS", data.created);
                }
                return data;
            } catch (error) {
                throw error;
            }
        },

        async cancelAssignment({ commit }, assignmentId) {
            try {
                let data = await ScheduleService.cancelAssignment(assignmentId);
                commit("UPDATE_ASSIGNMENT", data);
                return data;
            } catch (error) {
                throw error;
            }
        },

        async cancelAssignmentsByEngineer({ commit, dispatch }, { scheduleId, payload }) {
            try {
                let data = await ScheduleService.cancelAssignmentsByEngineer(scheduleId, payload);
                dispatch("fetchAssignments", scheduleId);
                return data;
            } catch (error) {
                throw error;
            }
        },

        async updateAssignmentStatus({ commit }, { assignmentId, payload }) {
            try {
                let data = await ScheduleService.updateAssignmentStatus(assignmentId, payload);
                commit("UPDATE_ASSIGNMENT", data);
                return data;
            } catch (error) {
                throw error;
            }
        },

        async updateAssignment({ commit }, { assignmentId, payload }) {
            try {
                let data = await ScheduleService.updateAssignment(assignmentId, payload);
                commit("UPDATE_ASSIGNMENT", data);
                return data;
            } catch (error) {
                throw error;
            }
        },
    },
};

export function buildQueryString(params) {
    params._t = Date.now();
    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
}

export function buildParams(currentTabState, state) {
    let params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
        tab: state.currentTab,
    };

    if (currentTabState.searchText) {
        params.searchTerm = currentTabState.searchText;
    }

    const formattedFromDate = formatDateToUKFormat(currentTabState.fromDate);
    if (formattedFromDate && formattedFromDate !== "Invalid date") {
        params.fromDate = formattedFromDate;
    }

    const formattedToDate = formatDateToUKFormat(currentTabState.toDate);
    if (formattedToDate && formattedToDate !== "Invalid date") {
        params.toDate = formattedToDate;
    }

    if (currentTabState.selectedEngineer && currentTabState.selectedEngineer.staff_id) {
        params.engineer_id = currentTabState.selectedEngineer.staff_id;
    }

    if (currentTabState.selectedScheduleStatus) {
        params.status = currentTabState.selectedScheduleStatus;
    }

    switch (state.currentTab) {
        case 'booked':
            params.is_managed = true;
            break;
        case 'completed':
            params.is_managed = true;
            break;
        case 'unbooked':
            params.isScheduled = false;
            params.tab = 'unscheduled';
            params.groupByCustomer = true;
            break;
        case 'follow_up':
            break;
    }
    if (state.currentTab === 'all_expiring') {
        params.groupByCustomer = true;
    }
    return params;
}

function formatDateToUKFormat(dateInput) {
    const date = typeof dateInput === 'string' ? moment(dateInput) : moment(dateInput);
    return date.format('DD/MM/YYYY');
}

export function getApiEndpoint(currentTab) {
    switch (currentTab) {
        case 'booked':
        case 'completed':
            return ScheduleService.getSchedules;
        case 'follow_up':
            return ScheduleFollowUpService.searchFollowUps;
        case 'all_expiring':
        case 'unbooked':
        default:
            return ScheduleService.searchEquipmentUpComingService;
    }
}

export function groupEquipmentByCustomer(equipmentList) {
    const map = {};
    equipmentList.forEach(item => {
        const owner = item.owner_info || {};
        const customerId = owner.contact_id || owner._id || 'unknown';

        if (!map[customerId]) {
            // The address and person marked default; the first only when none is marked
            const addresses = Array.isArray(owner.addresses) ? owner.addresses : [];
            const people = Array.isArray(owner.people) ? owner.people : [];
            const addr = addresses.find(a => a && a.is_default === true) || addresses[0] || {};
            const person = people.find(p => p && p.is_default === true) || people[0] || {};
            map[customerId] = {
                customer_id: customerId,
                customer_name: owner.name || 'Unknown',
                city: addr.city || '',
                postcode: addr.postcode || '',
                contact_phone: person.telephone || person.mobile || '',
                contact_email: person.email || '',
                equipment_count: 0,
                nearest_expiry: null,
                equipments: [],
            };
        }

        const svcExp = (typeof item.latest_service_expiry === 'number' && item.latest_service_expiry > 0) ? item.latest_service_expiry : null;
        const calExp = (typeof item.latest_calibration_expiry === 'number' && item.latest_calibration_expiry > 0) ? item.latest_calibration_expiry : null;
        const calLegacy = item.calibration_with_max_expiry ? item.calibration_with_max_expiry.cert_expiry_date : null;
        const candidates = [svcExp, calExp, calLegacy].filter(v => typeof v === 'number' && v > 0);
        const expiry = candidates.length ? Math.min.apply(null, candidates) : null;

        if (expiry && (!map[customerId].nearest_expiry || expiry < map[customerId].nearest_expiry)) {
            map[customerId].nearest_expiry = expiry;
        }

        map[customerId].equipment_count++;
        map[customerId].equipments.push({
            product_code: item.product_code || '',
            name: item.name || '',
            serial_number: item.serial_number || '',
            expiry_date: expiry,
            make: item.make || '',
            product_type: item.product_type || '',
            equipment_id: item.equipment_id || item._id || '',
        });
    });

    return Object.values(map);
}
